import { format } from "date-fns";
import { v4 as uuidv4 } from "uuid";

import { ISO8601_DATE_FORMAT } from "@constants";
import { formatDateWithTimezone } from "@utils";

import { ProductionType } from "../../../../shared/enums";
import { computeApplicationRate } from "../domain/techop-form/asset-totals";
import type { TechopFormValues } from "../domain/techop-form/techop-form-values";
import { ITechOperationDto, ITechOperationUpsertDto } from "../dtos/tech-operation.dto";
import type { ITechopEditingFormMetaData } from "../interfaces/techop-form-metadata";

import { TechOperationAsset } from "./tech-operation-asset";
import { TechOperationValue } from "./tech-operation-value";

export class TechOperation {
  readonly id: string;
  farmId: string;
  farmLandId: string;
  farmLandName: string;
  // release/2.1 hotfix for DEVELOPMENT-3626, DEVELOPMENT-3628
  realFarmLandId: string;
  realFarmLandName: string;
  cropTypeId: string;
  cropId?: string;
  startDate = new Date();
  finishDate = new Date();
  techOperationGroupId: string;
  techOperationSubGroupId: string;
  fieldSize: number;
  operationNumber: number;
  comment: string;
  productionType: ProductionType;
  techAssetId: string | null;
  techAssetName: string;
  trailerAssetId: string | null;
  trailerAssetName: string | null;
  employeeId: string;
  seasonId?: string;
  massCleanedProduct?: number;
  productivitySet?: number;
  createdOnClientAt: string | null;
  appUserId: string;
  techOperationValues: TechOperationValue[];
  materiallyAccountableEmployees: ITechOperationDto["materiallyAccountableEmployees"];
  assets: TechOperationAsset[];

  constructor(id: string = uuidv4()) {
    this.id = id;
  }

  static fromDto(dto: ITechOperationDto): TechOperation {
    const model = new TechOperation(dto.id);
    model.updateFromDto(dto);
    return model;
  }

  get isPredecessor(): boolean {
    return this.realFarmLandId !== undefined
      ? this.realFarmLandId !== this.farmLandId
      : false;
  }

  get isOriginal(): boolean {
    return this.realFarmLandId !== undefined
      ? this.realFarmLandId === this.farmLandId
      : true;
  }

  get asDto(): ITechOperationDto {
    return {
      id: this.id,
      farmId: this.farmId,
      seasonId: this.seasonId,
      farmLandId: this.farmLandId,
      farmLandName: this.farmLandName,
      realFarmLandId: this.realFarmLandId,
      realFarmLandName: this.realFarmLandName,
      cropTypeId: this.cropTypeId,
      cropId: this.cropId,
      startDate: format(this.startDate, ISO8601_DATE_FORMAT),
      finishDate: format(this.finishDate, ISO8601_DATE_FORMAT),
      techOperationGroupId: this.techOperationGroupId,
      techOperationSubGroupId: this.techOperationSubGroupId,
      fieldSize: this.fieldSize,
      operationNumber: this.operationNumber,
      comment: this.comment,
      productionType: this.productionType,
      techAssetId: this.techAssetId,
      techAssetName: this.techAssetName === "" ? undefined : this.techAssetName,
      trailerAssetId: this.trailerAssetId === "" ? null : this.trailerAssetId,
      trailerAssetName:
        this.trailerAssetName === "" ? undefined : (this.trailerAssetName ?? undefined),
      employeeId: this.employeeId === "" ? null : this.employeeId,
      massCleanedProduct: this.massCleanedProduct,
      productivitySet: this.productivitySet,
      createdOnClientAt: this.createdOnClientAt,
      appUserId: this.appUserId,
      materiallyAccountableEmployees: this.materiallyAccountableEmployees,
      techOperationValues: this.techOperationValues?.map((item) => item.asDto),
      assets: this.assets?.map((asset) => asset.asDto),
    };
  }

  get asUpsertDto(): ITechOperationUpsertDto {
    const { assets: _serverShapedAssets, ...rest } = this.asDto;
    return { ...rest, Asset: this.assets?.map((asset) => asset.asUpsertDto) };
  }

  updateFromDto(dto: ITechOperationDto): void {
    this.farmId = dto.farmId;
    this.seasonId = dto.seasonId;
    this.farmLandId = dto.farmLandId;
    this.farmLandName = dto.farmLandName;

    this.realFarmLandId = dto.realFarmLandId;
    this.realFarmLandName = dto.realFarmLandName;

    this.cropTypeId = dto.cropTypeId;
    this.cropId = dto.cropId;

    this.startDate = new Date(dto.startDate);
    this.finishDate = new Date(dto.finishDate);
    this.techOperationGroupId = dto.techOperationGroupId;
    this.techOperationSubGroupId = dto.techOperationSubGroupId;
    this.fieldSize = dto.fieldSize;
    this.operationNumber = dto.operationNumber;
    this.comment = dto.comment;

    this.productionType = dto.productionType;
    this.techAssetId = dto.techAssetId;
    this.techAssetName = dto.techAssetName ?? "";
    this.trailerAssetId = dto.trailerAssetId;
    this.trailerAssetName = dto.trailerAssetName ?? null;
    this.employeeId = dto.employeeId === null ? "" : dto.employeeId;

    this.massCleanedProduct = dto.massCleanedProduct;
    this.productivitySet = dto.productivitySet;
    this.createdOnClientAt = dto.createdOnClientAt;

    this.appUserId = dto.appUserId;

    this.techOperationValues =
      dto.techOperationValues?.map(TechOperationValue.fromDto) || [];
    this.materiallyAccountableEmployees = dto.materiallyAccountableEmployees;
    this.assets =
      dto.assets?.map((assetDto) => {
        const model = TechOperationAsset.fromDto(assetDto);
        return model;
      }) || [];
  }

  updateFromFormData(
    formData: TechopFormValues,
    metaData: ITechopEditingFormMetaData
  ): void {
    const { seedsMAE, chemicalsMAE, fertilisersMAE } = formData;
    const { farmId, seasonId, cropTypeId, cropId } = metaData;
    const { measureIdByOperationParamId } = metaData;

    this.farmId = farmId;
    this.seasonId = seasonId;
    this.cropTypeId = cropTypeId;
    this.cropId = cropId;

    this.farmLandId = formData.farmLandId || "";
    this.techOperationGroupId = formData.techOperationGroupId ?? "";
    this.techOperationSubGroupId = formData.techOperationSubGroupId ?? "";
    this.operationNumber = Number(formData.operationNumber);

    if (formData.startDate && formData.finishDate) {
      this.startDate = new Date(formData.startDate.getTime());
      this.finishDate = new Date(formData.finishDate.getTime());
    }

    this.fieldSize = Number(formData.fieldSize);
    this.comment = formData.comment ?? "";
    this.techAssetId =
      formData.techAssetId && formData.techAssetId !== "" ? formData.techAssetId : null;
    this.techAssetName = "";
    this.trailerAssetName = null;
    this.trailerAssetId =
      formData.trailerAssetId === "-1" ||
      formData.trailerAssetId === undefined ||
      formData.trailerAssetId === null
        ? null
        : formData.trailerAssetId;
    this.employeeId = formData.employeeId ?? "";

    this.techOperationValues = Object.keys(formData.params).map((operationParamKey) => {
      const { id, arrayParamId, value } = formData.params[operationParamKey];
      const operationParamId = Number(operationParamKey);
      const techOperationValue = new TechOperationValue(id);
      const resolvedArrayParamId =
        arrayParamId != null && arrayParamId !== 0 ? arrayParamId : null;
      techOperationValue.updateFromDto({
        id,
        operationParamId,
        arrayParamId: resolvedArrayParamId,
        measureId: measureIdByOperationParamId[operationParamId] ?? null,
        farmId,
        techOperationId: this.id,
        value: value ?? "",
      });
      return techOperationValue;
    });

    this.materiallyAccountableEmployees = {
      seeds: seedsMAE,
      chemicals: chemicalsMAE,
      fertilisers: fertilisersMAE,
    };

    this.assets = Object.values(formData.assets).map((assetRhf) => {
      const assetModel = new TechOperationAsset(assetRhf.id);
      assetModel.updateFromFormData(assetRhf);
      assetModel.applicationRate = computeApplicationRate(
        Number(assetRhf.quantityReal),
        formData.fieldSize
      );
      return assetModel;
    });
  }

  setAppUserId(appUserId: string): void {
    this.appUserId = appUserId;
  }

  setFarmLandId(farmLandId: string | null): TechOperation {
    if (farmLandId) {
      this.farmLandId = farmLandId;
    }
    return this;
  }

  updateEachAsset(farmId: string, techopId: string): void {
    for (const asset of this.assets) {
      // to avoid DUPLICATES of "new TechOperationAsset(assetRhf._dtoId).updateFromFormData(assetRhf)",
      // we change fields in an existing object;
      // we don't create new asset[] with new instances inside
      asset.setFarmId(farmId);
      asset.setTechOperationId(techopId);
    }
  }

  resetCreatedOnClientAt(): void {
    const now = new Date();
    const newDate = formatDateWithTimezone(now);
    this.createdOnClientAt = newDate;
  }
}
